import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedUserIcon,
  Cancel as CancelIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCustomer,
  useCustomerCreditScore,
  useCustomerLoanLimit,
  useBlockCustomer,
  useUnblockCustomer,
  useVerifyKYC,
  useCustomerDocuments,
} from '../../hooks/customers/useCustomers';
import { useLoans } from '../../hooks/loans/useLoans';
import { useAuth } from '../../hooks/auth/useAuth';
import { format } from 'date-fns';

const formatDocumentType = (type: string) =>
  type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const safeFormatDate = (value: string | null | undefined, fmt: string) => {
  if (!value) return '—';
  // Normalize "2026-02-21 06:46:52.276074" → "2026-02-21T06:46:52.276074"
  const d = new Date(value.replace(' ', 'T'));
  return isNaN(d.getTime()) ? '—' : format(d, fmt);
};

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const customerId = id || '';

  // Dialog state
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionDocumentType, setRejectionDocumentType] = useState<string | undefined>(undefined);
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch data
  const { data: customer, isLoading: customerLoading, error: customerError } = useCustomer(customerId);
  const { data: creditScore, isLoading: creditScoreLoading } = useCustomerCreditScore(customerId);
  const { data: loanLimit, isLoading: loanLimitLoading } = useCustomerLoanLimit(customerId);
  const { data: customerLoans, isLoading: loansLoading } = useLoans({ customerId });
  const { data: documentsData, isLoading: documentsLoading } = useCustomerDocuments(customerId);

  // Mutations
  const blockMutation = useBlockCustomer();
  const unblockMutation = useUnblockCustomer();
  const verifyKYCMutation = useVerifyKYC();

  const documents = documentsData?.documents ?? [];
  const hasPendingDocuments = documents.some((d) => d.status === 'PENDING');

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'VERIFIED': return 'success';
      case 'PENDING':  return 'warning';
      case 'REJECTED': return 'error';
      default:         return 'default';
    }
  };

  const getLoanStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':                  return 'success';
      case 'PAID':                    return 'info';
      case 'OVERDUE':                 return 'warning';
      case 'DEFAULTED':
      case 'WRITTEN_OFF':             return 'error';
      default:                        return 'default';
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleBlockCustomer = async () => {
    if (!customerId || !blockReason.trim() || !user) return;
    await blockMutation.mutateAsync({ customerId, data: { reason: blockReason, blockedBy: user.userId } });
    setBlockDialogOpen(false);
    setBlockReason('');
  };

  const handleUnblockCustomer = async () => {
    if (!customerId) return;
    await unblockMutation.mutateAsync(customerId);
  };

  /** Approve all documents (bulk) */
  const handleApproveAll = async () => {
    if (!customerId || !user) return;
    await verifyKYCMutation.mutateAsync({
      customerId,
      data: { approved: true, reviewedBy: user.userId },
    });
    setKycDialogOpen(false);
  };

  /** Approve a single document directly (no reason needed) */
  const handleApproveDoc = async (documentType: string) => {
    if (!customerId || !user) return;
    await verifyKYCMutation.mutateAsync({
      customerId,
      data: { documentType, approved: true, reviewedBy: user.userId },
    });
  };

  /** Open rejection dialog — for a specific doc or for all (undefined) */
  const openRejectionDialog = (documentType?: string) => {
    setRejectionDocumentType(documentType);
    setRejectionReason('');
    setKycDialogOpen(false);
    setRejectionDialogOpen(true);
  };

  const handleConfirmRejection = async () => {
    if (!customerId || !user) return;
    await verifyKYCMutation.mutateAsync({
      customerId,
      data: {
        documentType: rejectionDocumentType,
        approved: false,
        rejectionReason: rejectionReason,
        reviewedBy: user.userId,
      },
    });
    setRejectionDialogOpen(false);
    setRejectionReason('');
    setRejectionDocumentType(undefined);
  };

  // ── Loading / error states ────────────────────────────────────────────────

  if (customerError) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mb: 2 }}>
          Back to Customers
        </Button>
        <Alert severity="error">
          Failed to load customer details.{' '}
          {customerError instanceof Error ? customerError.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  if (customerLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mb: 2 }}>
          Back to Customers
        </Button>
        <Alert severity="warning">Customer not found</Alert>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mb: 2 }}>
            Back to Customers
          </Button>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {customer.fullName}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              label={customer.kycStatus}
              size="small"
              color={getStatusColor(customer.kycStatus) as 'success' | 'warning' | 'error' | 'default'}
            />
            <Chip
              label={customer.isBlocked ? 'Blocked' : 'Active'}
              size="small"
              color={customer.isBlocked ? 'error' : 'success'}
              icon={customer.isBlocked ? <BlockIcon /> : <CheckCircleIcon />}
            />
          </Stack>
        </Box>

        <Stack direction="row" spacing={1}>
          {(hasPendingDocuments || customer.kycStatus !== 'VERIFIED') && (
            <Button
              variant="contained"
              startIcon={<VerifiedUserIcon />}
              onClick={() => setKycDialogOpen(true)}
            >
              Verify KYC
            </Button>
          )}
          {customer.isBlocked ? (
            <Button
              variant="outlined"
              color="success"
              onClick={handleUnblockCustomer}
              disabled={unblockMutation.isPending}
            >
              {unblockMutation.isPending ? 'Unblocking...' : 'Unblock Customer'}
            </Button>
          ) : (
            <Button variant="outlined" color="error" onClick={() => setBlockDialogOpen(true)}>
              Block Customer
            </Button>
          )}
        </Stack>
      </Box>

      <Stack spacing={3}>
        {/* Personal & Financial Information */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">ID Number</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.idNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Phone Number</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.phoneNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.email}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {safeFormatDate(customer.dateOfBirth, 'MMM dd, yyyy')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Gender</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.gender}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Customer Type</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.customerType}</Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="body2" color="text.secondary">Residential Address</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.residentialAddress}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">County</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.county}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Sub County</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.subCounty}</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Financial Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              {customer.employerName && (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Employer</Typography>
                    <Typography variant="body1" fontWeight="medium">{customer.employerName}</Typography>
                  </Box>
                  {customer.employmentStartDate && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Employment Start Date</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {format(new Date(customer.employmentStartDate), 'MMM dd, yyyy')}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">Monthly Income</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatCurrency(customer.monthlyIncome)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">M-PESA Number</Typography>
                <Typography variant="body1" fontWeight="medium">{customer.mpesaNumber}</Typography>
              </Box>
              {customer.bankName && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Bank Name</Typography>
                    <Typography variant="body1" fontWeight="medium">{customer.bankName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Account Number</Typography>
                    <Typography variant="body1" fontWeight="medium">{customer.bankAccountNumber}</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>

        {/* KYC Documents */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              KYC Documents
            </Typography>
            {hasPendingDocuments && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<ThumbUpIcon />}
                  onClick={handleApproveAll}
                  disabled={verifyKYCMutation.isPending}
                >
                  Approve All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ThumbDownIcon />}
                  onClick={() => openRejectionDialog(undefined)}
                  disabled={verifyKYCMutation.isPending}
                >
                  Reject All
                </Button>
              </Stack>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          {documentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : documents.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No KYC documents uploaded yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Document Type</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Uploaded</strong></TableCell>
                    <TableCell><strong>Rejection Reason</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.documentId} hover>
                      <TableCell>{formatDocumentType(doc.documentType)}</TableCell>
                      <TableCell>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={getStatusColor(doc.status) as 'success' | 'warning' | 'error' | 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {safeFormatDate(doc.uploadedAt, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        {doc.rejectionReason ? (
                          <Typography variant="body2" color="error.main">
                            {doc.rejectionReason}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {doc.status === 'PENDING' && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Approve">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApproveDoc(doc.documentType)}
                                  disabled={verifyKYCMutation.isPending}
                                >
                                  <ThumbUpIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => openRejectionDialog(doc.documentType)}
                                  disabled={verifyKYCMutation.isPending}
                                >
                                  <ThumbDownIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Credit Score & Loan Limit */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Credit Score</Typography>
            <Divider sx={{ mb: 2 }} />
            {creditScoreLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : creditScore ? (
              <>
                <Typography variant="h3" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                  {creditScore.creditScore}
                </Typography>
                <Chip
                  label={creditScore.riskCategory}
                  color={
                    creditScore.riskCategory === 'LOW'
                      ? 'success'
                      : creditScore.riskCategory === 'MEDIUM'
                      ? 'warning'
                      : 'error'
                  }
                  sx={{ mb: 2 }}
                />
                {creditScore.scoreDetails && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Payment History</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {creditScore.scoreDetails.paymentHistory}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Loan History</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {creditScore.scoreDetails.loanHistory}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Credit Utilization</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {creditScore.scoreDetails.creditUtilization}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Account Age</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {creditScore.scoreDetails.accountAge}%
                      </Typography>
                    </Box>
                  </Stack>
                )}
                {creditScore.lastUpdated && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                    Last updated: {format(new Date(creditScore.lastUpdated), 'MMM dd, yyyy')}
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.secondary">No credit score available</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Loan Limit</Typography>
            <Divider sx={{ mb: 2 }} />
            {loanLimitLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : loanLimit ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Current Loan Limit</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(loanLimit.currentLoanLimit)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Available Limit</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(loanLimit.availableLimit)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Outstanding Loans</Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {formatCurrency(loanLimit.outstandingLoans)}
                    </Typography>
                  </Box>
                </Box>
                {loanLimit.basedOnCreditScore && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Loan limit is calculated based on credit score
                  </Alert>
                )}
              </>
            ) : (
              <Typography color="text.secondary">No loan limit information available</Typography>
            )}
          </Paper>
        </Box>

        {/* Loan History */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Loan History</Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Loan Number</strong></TableCell>
                  <TableCell><strong>Principal</strong></TableCell>
                  <TableCell><strong>Outstanding</strong></TableCell>
                  <TableCell><strong>Paid</strong></TableCell>
                  <TableCell><strong>Due Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loansLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : customerLoans && customerLoans.length > 0 ? (
                  customerLoans.map((loan) => (
                    <TableRow key={loan.loanId} hover>
                      <TableCell>{loan.loanNumber}</TableCell>
                      <TableCell>{formatCurrency(loan.principalAmount)}</TableCell>
                      <TableCell>{formatCurrency(loan.amountOutstanding)}</TableCell>
                      <TableCell>{formatCurrency(loan.amountPaid)}</TableCell>
                      <TableCell>
                        {format(new Date(loan.dueDate), 'MMM dd, yyyy')}
                        {loan.daysOverdue > 0 && (
                          <Typography variant="caption" color="error" display="block">
                            {loan.daysOverdue} days overdue
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={loan.loanStatus}
                          size="small"
                          color={getLoanStatusColor(loan.loanStatus) as 'success' | 'warning' | 'error' | 'info' | 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/loans/${loan.loanId}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No loan history</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Additional Information */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Additional Information</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Customer ID</Typography>
              <Typography variant="body1" fontWeight="medium">{customer.customerId}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Registered On</Typography>
              <Typography variant="body1" fontWeight="medium">
                {format(new Date(customer.createdAt), 'MMM dd, yyyy HH:mm')}
              </Typography>
            </Box>
            {customer.isBlocked && customer.blockReason && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Alert severity="error">
                  <Typography variant="body2" fontWeight="medium">
                    Block Reason: {customer.blockReason}
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}

      {/* Bulk KYC Dialog */}
      <Dialog open={kycDialogOpen} onClose={() => setKycDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Review All KYC Documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Approve or reject all pending KYC documents for this customer at once.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKycDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => openRejectionDialog(undefined)}
            color="error"
            disabled={verifyKYCMutation.isPending}
            startIcon={<CancelIcon />}
          >
            Reject All
          </Button>
          <Button
            onClick={handleApproveAll}
            variant="contained"
            disabled={verifyKYCMutation.isPending}
            startIcon={<CheckCircleIcon />}
          >
            {verifyKYCMutation.isPending ? 'Approving...' : 'Approve All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Reason Dialog (per-doc or bulk) */}
      <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {rejectionDocumentType
            ? `Reject ${formatDocumentType(rejectionDocumentType)}`
            : 'Reject All Documents'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {rejectionDocumentType
              ? 'Provide a reason for rejecting this document.'
              : 'Provide a reason for rejecting all pending documents.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRejection}
            color="error"
            variant="contained"
            disabled={verifyKYCMutation.isPending || !rejectionReason.trim()}
          >
            {verifyKYCMutation.isPending ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Customer Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block Customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to block this customer? They will not be able to apply for new loans.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Block Reason"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBlockCustomer}
            color="error"
            variant="contained"
            disabled={blockMutation.isPending || !blockReason.trim()}
          >
            {blockMutation.isPending ? 'Blocking...' : 'Block Customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDetails;
